import React, { useContext, useState } from "react"
import { Alert, Badge, Button, Card, CardBody, Progress, Spinner, Table } from "reactstrap"

import FieldDiff from "./FieldDiff"
import LlmInstrContext from "./LlmInstrContext"
import MessageModal from "./MessageModal"
import {
  DEFAULT_SAMPLE_DAYS, SCOPE_QD_REF, STRATEGY_LABELS, STRATEGY_SOURCE_NOTES,
} from "./constants"

/**
 * "Confirmed · 09-29 13:30 · code 4471902" -- one readable line rather than a stacked cell.
 *
 * A group reply has no flat status to show: its decision is per appointment, so the line
 * becomes the group verdict plus how many of its appointments were confirmed. Summarising a
 * group with appointments[0] would quietly report one stop's outcome as the whole reply's.
 */
function describe(s) {
  if (!s) return "—"
  if (s.appointments) {
    const appts = s.appointments || []
    const confirmed = s.appointments_confirmed
    const total = s.total_appointments_requested ?? appts.length
    const verdict = s.appointment_status === "CONFIRMED" ? "All confirmed" : (s.appointment_status || "—")
    return `${verdict} · ${confirmed ?? "?"}/${total ?? "?"} appts`
  }
  const bits = [s.status || "—"]
  if (s.conf_date_tm_from) bits.push(String(s.conf_date_tm_from).replace("T", " ").slice(5, 16))
  if (s.confirmation_code) bits.push(`code ${s.confirmation_code}`)
  return bits.join(" · ")
}

// Only "major" is coloured. A minor or absent difference still gets a badge so the row has
// a visible verdict, but a neutral one -- colouring it would compete with the rows that
// actually need looking at.
const IMPACT_COLOR = { major: "warning", minor: "light", none: "light" }

// Rows are identified by the PAIR: a "validate for this QD Ref" run is several replies
// sharing one mail_tracker, so the tracker alone would expand them all at once.
const rowKey = r => `${r.mail_tracker}-${r.message_id}`

function Row({ r, materialOf, showHuman, expanded, onToggle, onView, markTrigger }) {
  const material = materialOf(r)
  const changed = r.changed_fields || []
  const isOpen = expanded === rowKey(r)
  const impact = r.impact || { level: material.length ? "major" : changed.length ? "minor" : "none", label: "" }

  const rowClass = material.length ? "table-warning" : undefined
  return (
    <>
      <tr className={rowClass}>
        <td className="text-nowrap align-middle">
          <Button color="link" size="sm" className="p-0 text-decoration-none"
                  onClick={() => onToggle(isOpen ? null : rowKey(r))}>
            {isOpen ? "▾" : "▸"} details
          </Button>
        </td>
        <td className="align-middle">
          {/* Only worth saying when the run mixed the pasted reply in with others. A QD Ref
              run is entirely replies under that ref -- the header says so -- and repeating
              it per row just crowds the reply text. */}
          {markTrigger && r.is_trigger &&
            <Badge color="dark" className="me-1">the one you pasted</Badge>}
          {/* Which strategy replayed this reply. Shown because the Before/After columns read
              differently for a group row, and because a reply is only comparable with the
              others that ran the same way. */}
          {r.confirmation_strategy && (
            <Badge color="light" className="text-dark border me-1 fw-normal"
                   title={`Replayed under the ${STRATEGY_LABELS[r.confirmation_strategy] || ""} `
                          + `strategy (${r.confirmation_strategy})`}>
              {STRATEGY_LABELS[r.confirmation_strategy] || r.confirmation_strategy}
            </Badge>
          )}
          <span className="text-muted small">
            {r.response_rcvd_on ? r.response_rcvd_on.slice(0, 10) + " · " : ""}
          </span>
          {/* The reply text IS the link to the full message. The instruction gets written
              by quoting the reply, so the whole text has to be reachable from the row that
              reports on it -- but from the thing being read, not a separate narrow link
              beside it. */}
          <Button color="link" size="sm"
                  className="p-0 text-start text-decoration-none align-baseline"
                  title="Open the full reply"
                  onClick={() => onView(r)}>
            {r.snippet || "(empty)"}
          </Button>
        </td>
        <td className="align-middle small">{describe(r.before)}</td>
        <td className="align-middle small fw-bold">{describe(r.after)}</td>
        {showHuman && (
          <td className="align-middle">
            {r.human_said ? <Badge color="info">{r.human_said}</Badge>
                          : <span className="text-muted">—</span>}
          </td>
        )}
        {/* Every row gets an explicit verdict here, including "No change". This column is
            the per-row answer to "what would my draft do to this reply?", so a row that
            renders nothing in it reads as a row that was not tested. */}
        <td className="align-middle">
          {r.error ? (
            <Badge color="danger">failed</Badge>
          ) : (
            <Badge color={IMPACT_COLOR[impact.level] || "light"}
                   className="text-dark text-wrap text-start fw-normal border">
              {impact.label || (changed.length ? "Minor fields only" : "No change")}
            </Badge>
          )}
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={showHuman ? 6 : 5} className="bg-light">
            {/* Named here as well as on the row: the two halves of the diff only mean
                anything if they came from the same strategy, and which one it was decides
                what the field names below even are (flat, or per appointment). */}
            {r.confirmation_strategy && (
              <div className={`small mb-1 ${r.strategy_source === "default" ? "text-warning-emphasis" : "text-muted"}`}>
                Replayed under <strong>{STRATEGY_LABELS[r.confirmation_strategy] || r.confirmation_strategy}</strong>{" "}
                (<code>{r.confirmation_strategy}</code>)
                {STRATEGY_SOURCE_NOTES[r.strategy_source] &&
                  <> — {STRATEGY_SOURCE_NOTES[r.strategy_source]}</>}
              </div>
            )}
            {r.after?.would_be_ignored_as_automated && (
              <Alert color="warning" className="py-1 px-2 small">
                With this instruction the quorum flags this reply as a purely automated
                message, so production would ignore it outright — no ack, no forward, no
                followup change.
              </Alert>
            )}
            {r.error && <Alert color="danger" className="py-1 px-2 small">{r.error}</Alert>}
            <FieldDiff before={r.before} after={r.after} changedFields={changed} />
          </td>
        </tr>
      )}
    </>
  )
}

function PreviewResults() {
  const { resolved, job, scope, rows, materialOf, previewing } =
    useContext(LlmInstrContext)
  const [expanded, setExpanded] = useState(null)
  const [viewing, setViewing] = useState(null)

  if (!resolved) return null
  const samples = resolved.samples || []
  const scopeLabel = scope === SCOPE_QD_REF
    ? "the replies under this QD Ref"
    : `this location's last ${samples.length || ""} replies`.replace("  ", " ")

  // ---- nothing run yet -------------------------------------------------
  if (!job && !previewing) {
    return (
      <Card>
        <CardBody>
          <h6 className="mb-1">Nothing validated yet</h6>
          <p className="text-muted small mb-2">
            Write an instruction on the left, then validate it. Either run replays that
            instruction over stored replies and shows which ones the appointment system
            would read differently — <strong>Validate for QD Ref</strong> over the replies
            listed above, <strong>Validate for last 10</strong> over this location&apos;s
            recent history.
          </p>
          {samples.length === 0 ? (
            <Alert color="info" className="small mb-0">
              No other processed replies at this location in the last {DEFAULT_SAMPLE_DAYS}{" "}
              days, so only the QD Ref run has anything to test against.
            </Alert>
          ) : (
            <p className="small mb-0">
              <strong>{samples.length}</strong> recent repl{samples.length === 1 ? "y" : "ies"}{" "}
              available for the wider run.
            </p>
          )}
        </CardBody>
      </Card>
    )
  }

  const counts = job?.result?.counts || {}
  const total = counts.total || samples.length + 1
  const done = rows.length

  const material = rows.filter(r => materialOf(r).length > 0)
  const minor = rows.filter(r => materialOf(r).length === 0 && (r.changed_fields || []).length > 0)
  const same = rows.filter(r => !(r.changed_fields || []).length)
  // The column is pure noise when nothing was ever manually reviewed, which is the norm.
  const showHuman = rows.some(r => r.human_said)
  const rowProps = { materialOf, showHuman, expanded, onToggle: setExpanded,
                     onView: setViewing, markTrigger: scope !== SCOPE_QD_REF }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1">What your instruction would change</h6>
            <div className="text-muted" style={{ fontSize: "0.72rem" }}>
              validated against {scopeLabel}
            </div>
            {previewing ? (
              <span className="small text-muted">
                <Spinner size="sm" className="me-1" />
                testing {done} of {total} replies…
              </span>
            ) : (
              <span className="small">
                {material.length === 0 ? (
                  <span className="text-success">
                    No reply would be decided differently.
                  </span>
                ) : (
                  <span>
                    <strong className="text-warning-emphasis">
                      {material.length} of {rows.length}
                    </strong>{" "}
                    {material.length === 1 ? "reply would be" : "replies would be"} decided
                    differently.
                  </span>
                )}
                {minor.length > 0 && (
                  <span className="text-muted"> {minor.length} differed only on minor fields.</span>
                )}
              </span>
            )}
          </div>
          {!previewing && counts.llm_calls ? (
            <span className="text-muted small text-nowrap">{counts.llm_calls} LLM calls</span>
          ) : null}
        </div>

        {previewing && (
          <Progress className="mb-2" value={total ? (done / total) * 100 : 0} style={{ height: 4 }} />
        )}

        {job?.status === "FAILED" && (
          <Alert color="danger" className="py-2 small">{job.error || "The preview failed."}</Alert>
        )}

        <Table bordered hover size="sm" className="small mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: 78 }}></th>
              <th>Facility reply</th>
              <th style={{ width: 170 }}>Before</th>
              <th style={{ width: 170 }}>After</th>
              {showHuman && <th style={{ width: 104 }}>Reviewer said</th>}
              <th style={{ width: 200 }}>Impact of change</th>
            </tr>
          </thead>
          <tbody>
            {/* One flat list, most consequential first -- no group headers and nothing
                collapsed. Every reply that was tested gets a visible row, and the Impact of
                change column already states that row's verdict, so grouping by the same
                thing the column says only buried the unchanged ones behind a count. */}
            {[...material, ...minor, ...same].map(r => (
              <Row key={rowKey(r)} r={r} {...rowProps} />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={showHuman ? 6 : 5} className="text-center text-muted">
                  {previewing ? "Waiting for the first reply…" : "No rows."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        <MessageModal reply={viewing} onClose={() => setViewing(null)} />

        <div className="text-muted mt-2" style={{ fontSize: "0.72rem" }}>
          Rows are ordered by impact, most consequential first; every reply tested is listed.
          The tag beside each reply names the strategy it was replayed under — the same one
          that originally processed it.{" "}
          <strong>Before</strong> is what the appointment system recorded when this reply
          actually came in. <strong>After</strong> is what it would decide with this draft
          saved. <strong>Impact of change</strong> is derived automatically from the two.
        </div>
      </CardBody>
    </Card>
  )
}

export default PreviewResults
